import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import Input, { StyledInput } from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import TimeInput from 'components/final-form/TimeInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import createDecorator from 'final-form-calculate';
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, Loader, RadioProps, Table } from 'semantic-ui-react';
import { getRapidTestCodes } from 'services/covidProductsServices';
import axios from 'services/http';
import {
  setControlledByBookingStatus,
  setResultRejectedBookingStatus,
  setTestResult,
  verifyTestResult
} from 'services/interventionBookingServices';
import { getTestResultsForPatient } from 'services/patientRecordServices';
import styled from 'styled-components';
import {
  CovidTestSampleMethod,
  InterventionBookingStatus,
  InterventionBookingTestResult,
  InterventionExternalCode,
  TestResultStatus
} from 'ts-types/api.enums';
import { InterventionTestBookingSearchResult, TestResultDto } from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { ctValidator, required } from 'utils/validatorUtils';

const StyledForm = styled.form`
  padding: 0.75rem;
  width: 100%;

  .patient-data {
    font-size: 1.1rem;
    font-weight: 500;

    .barcode {
      color: #7a97ff;
    }
  }

  .field-label {
    margin-top: 0.4rem;
    margin-bottom: 0.7rem;

    .inline-span {
      display: inline-block;
      margin-right: 1rem;
    }
  }

  .field-label:not(:first-child) {
  }

  .multi-field-row {
    display: flex;
    flex-direction: row;

    & > div {
      margin-right: 1.5rem;
    }

    .date-input {
      width: 8rem;
    }

    .time-input .ui.mini.input {
      width: 6rem;
    }
  }

  .radio-group .ui.radio.checkbox {
    margin-right: 1.5rem;
  }

  & .ui.grid {
    padding-left: 0 !important;
  }

  .field-error {
    color: darkred;
    font-size: 1rem;
  }

  .loader-box {
    position: relative;
    width: 300px;
    height: 90px;
  }

  .test-result-warning {
    color: #b58105;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .test-result-error {
    color: darkred;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .previous-test-results {
    width: 100%;
    border-top: 1px solid #c5c5c5;
    padding-top: 0.5rem;

    .no-results-message {
      font-style: italic;
      color: grey
    }
  }

  .actions-row {
    margin-top: 2rem;

    button {
      margin-right: 1rem;
    }
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledCtInput = styled(StyledInput)`
  margin-top: 0.4rem;
  width: 70px;
`;

const StyledTableRow = styled(Table.Row)`

  &.revoked {
    color: rgb(198, 198, 203)
  }

  & .resultStatusIcon {
    margin-left: .5rem
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, PatientCentralRefDataConsumerRenderProps {
  interventionBooking: InterventionTestBookingSearchResult,
  onClose: () => void,
  onCloseAndUpdate: (bookingId: number) => void
}

interface State {
  requiredDataLoaded: boolean,
  testResults: RadioProps[],
  sampleMethods: RadioProps[],
  labOptions: DropdownOption[],
  analyzerOptions: DropdownOption[],
  rapidTestCodeOptions: DropdownOption[],
  testIntervention?: InterventionExternalCode,
  previousTestResults?: TestResultDto[],
  includingRevoked: boolean,
  modifySampleDateTime: boolean,
  testResultInProgress: boolean,
  testResultSaveError?: string,
  testResultSaveWarning?: string,
  errorMessage?: string
}

class SetTestResultModal extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<InterventionTestBookingSearchResult>;

  constructor(props: Props) {
    super(props);

    const booking = props.interventionBooking;

    let testIntervention = undefined;
    const intervention = props.getInterventionById(booking.interventionId);
    if (intervention) {
      testIntervention = InterventionExternalCode[intervention.externalCode];
    }

    const externalCode = intervention && intervention.externalCode;
    const rapidTest = externalCode === InterventionExternalCode.RAPID_SALIVA_TEST;

    let rapidTestCode = booking.rapidTestCode;
    const locationId = booking.locationId;
    if (rapidTest && !rapidTestCode && locationId) {
      const resourceDto = props.resources.find(res => res.id === locationId);
      rapidTestCode = resourceDto && resourceDto.rapidTestCode ? resourceDto.rapidTestCode : rapidTestCode;
    }

    this.initialValues = {
      testResult: booking.testResult,
      status: booking.status,
      ct: booking.ct,
      testSampleMethod: booking.testSampleMethod,
      testRegistrationDate: booking.testRegistrationDate,
      testRegistrationTime: booking.testRegistrationTime,
      rapidTestCode,
      laboratoryId: booking.laboratoryId,
      analyzerId: booking.analyzerId,
      remarks: booking.remarks
    };

    const testResults = Object.values(InterventionBookingTestResult)
        .filter(tr => tr !== InterventionBookingTestResult.POOL_POSITIVE
            && tr !== InterventionBookingTestResult.BORDERLINE);

    let testResultRadioProps = generateRadioButtonOptionsFromEnum(
        testResults, "enum.testResult", props.getMessage);

    const covidTestSampleMethods = [
      CovidTestSampleMethod.NASAL_SWAB,
      CovidTestSampleMethod.THROAT_SWAB,
      CovidTestSampleMethod.SALIVA
    ];
    const sampleMethodProps = generateRadioButtonOptionsFromEnum(
        covidTestSampleMethods, "covidTestSampleMethod", props.getMessage);

    const filteredAnalyzers = booking.laboratoryId
        ? props.covidTestAnalyzers.filter(analyzer => analyzer.laboratoryId === booking.laboratoryId)
        : props.covidTestAnalyzers;

    const analyzerOptions: DropdownOption[] =
        mapToDropdownOptionArrayWithSep(filteredAnalyzers, ["code", "description"], "id");

    this.state = {
      requiredDataLoaded: false,
      testResults: testResultRadioProps,
      sampleMethods: sampleMethodProps,
      labOptions: mapToDropdownOptionArray(props.laboratories, "name"),
      analyzerOptions,
      rapidTestCodeOptions: [],
      includingRevoked: false,
      testIntervention: testIntervention,
      modifySampleDateTime: false,
      testResultInProgress: false
    };

    this.fetchRequiredData(props);
  }

  fetchRequiredData = async (props: Props) => {

    const patientId = props.interventionBooking.patientId;

    const testResultDtos = await getTestResultsForPatient(patientId, false, this.cancelTokenSource);

    const rapidTestCodes = await getRapidTestCodes(this.cancelTokenSource);
    const rapidTestCodeOptions = mapToDropdownOptionArray(rapidTestCodes, "display", "code", "code");

    this.setState({
      requiredDataLoaded: true,
      previousTestResults: testResultDtos,
      rapidTestCodeOptions: rapidTestCodeOptions
    })
  }

  setTestKitResult = async (
      interventionBooking: InterventionTestBookingSearchResult,
      values: Partial<InterventionTestBookingSearchResult>,
      testResult?: string) => {

    const {ct, testRegistrationDate, testRegistrationTime} = values;

    this.setState({
      testResultInProgress: true
    });

    const testResultAfterUpdate = testResult || interventionBooking.testResult;

    const registrationTimestamp = this.state.modifySampleDateTime
        ? `${testRegistrationDate}T${testRegistrationTime}`
        : undefined;

    let ctValue = testResultAfterUpdate !== 'POSITIVE' ? undefined : ct;

    const testSampleMethod = values.testSampleMethod;

    const rapidTestCode = values.rapidTestCode;

    const laboratoryId = values.laboratoryId;
    const analyzerId = values.analyzerId;
    const remarks = values.remarks;

    try {
      const resultUpdateDto =
          await setTestResult(
              interventionBooking.bookingId,
              this.props.options.currentTreater.id,
              testResult,
              registrationTimestamp,
              testSampleMethod,
              rapidTestCode,
              ctValue,
              laboratoryId,
              analyzerId,
              remarks,
              this.cancelTokenSource);

      this.setState({
        testResultInProgress: false
      });

      if (resultUpdateDto.certificateError) {
        console.log(resultUpdateDto);
        this.setState({
          testResultSaveWarning:
              "patientCentral.overview.testResult.warn.officialCertificateNotCreated"
        });

        return;
      }

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  verifyTestKitResult = async (
      interventionBooking: InterventionTestBookingSearchResult,
      values: Partial<InterventionTestBookingSearchResult>,
      testResult?: string) => {

    const {ct, testRegistrationDate, testRegistrationTime} = values;

    this.setState({
      testResultInProgress: true
    });

    const testResultAfterUpdate = testResult || interventionBooking.testResult;

    const registrationTimestamp = this.state.modifySampleDateTime
        ? `${testRegistrationDate}T${testRegistrationTime}`
        : undefined;

    let ctValue = testResultAfterUpdate !== 'POSITIVE' ? undefined : ct;

    const testSampleMethod = values.testSampleMethod;

    const rapidTestCode = values.rapidTestCode;

    const laboratoryId = values.laboratoryId;
    const analyzerId = values.analyzerId;
    const remarks = values.remarks;

    try {
      await verifyTestResult(
          interventionBooking.bookingId,
          this.props.options.currentTreater.id,
          testResult,
          registrationTimestamp,
          testSampleMethod,
          rapidTestCode,
          ctValue,
          laboratoryId,
          analyzerId,
          remarks,
          this.cancelTokenSource);

      this.setState({
        testResultInProgress: false
      });

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  setTestResultInProgress = (testResultInProgress: boolean) => {
    this.setState({
      testResultInProgress
    })
  }

  setControlledBy = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setControlledByBookingStatus(bookingId, treaterId, this.cancelTokenSource);
      console.log(response);
      if (response.certificateError) {
        this.setState({
          testResultSaveWarning:
              "patientCentral.overview.testResult.warn.officialCertificateNotCreated"
        });

        return;
      }
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  setResultRejected = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setResultRejectedBookingStatus(bookingId, treaterId, this.cancelTokenSource);
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors = [
      errorUtils.testBookingWithCanceledStatus
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  closeAndUpdate = () => {
    this.props.onCloseAndUpdate(this.props.interventionBooking.bookingId);
  };

  parseCtInput = (value: any) => {
    if (!value || value.length < 6) {
      return value;
    } else {
      return value.slice(0, 5);
    }
  };

  setPreviousTestResultsForPatient = async () => {
    const {interventionBooking} = this.props;
    const patientId = interventionBooking.patientId;

    const prevTestResults = await getTestResultsForPatient(
        patientId, this.state.includingRevoked, this.cancelTokenSource);

    this.setState({
      previousTestResults: prevTestResults
    });
  }

  handleCheckBoxChange = (): void => {

    this.setState(
        (oldState) => {
          const newIncludingRevokedState = !oldState.includingRevoked;
          return {includingRevoked: newIncludingRevokedState}
        },
        () => {
          this.setPreviousTestResultsForPatient();
        });
  };

  changeCovidTestAnalyzerOptions = createDecorator({
    field: 'laboratoryId',
    updates: {
      analyzerId: (fieldValue, allValues?: Partial<InterventionTestBookingSearchResult>) => {
        if (fieldValue) {
          const filteredAnalyzers =
              this.props.covidTestAnalyzers.filter(analyzer => analyzer.laboratoryId === fieldValue);

          const analyzerOptions: DropdownOption[] =
              mapToDropdownOptionArrayWithSep(filteredAnalyzers, ["code", "description"], "id");

          this.setState({
            analyzerOptions
          });

          if (filteredAnalyzers.length) {
            const filteredAnalyzerIds: number[] = _.map(filteredAnalyzers, 'id');
            if (!allValues
                || !allValues.analyzerId
                || (allValues.analyzerId && !filteredAnalyzerIds.includes(allValues.analyzerId))
            ) {

              return filteredAnalyzers[0].id;
            } else {
              return allValues.analyzerId;
            }
          }
        }

        return undefined;
      }
    }
  });

  render(): React.ReactNode {

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Loader active/>
      </div>;
    }

    return (
        <FinalForm
            onSubmit={noop}
            initialValues={this.initialValues}
            decorators={[this.changeCovidTestAnalyzerOptions]}
            subscription={{values: true, pristine: true, submitting: true, errors: true, error: true}}
            render={this.renderForm}
        />
    );
  };

  renderForm = (
      {values, form, handleSubmit, submitting, errors}: FormRenderProps): React.ReactNode => {

    const {interventionBooking, getMessage} = this.props;
    const {testIntervention, testResults, includingRevoked, previousTestResults, errorMessage} = this.state;

    const {testResult, status} = values;

    const ctError = Object.keys(errors).includes("ct");

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Dimmer active={true}>
          <Loader inverted inline="centered" size="medium">
            {getMessage("patientCentral.loader.message")}
          </Loader>
        </Dimmer>
      </div>;
    }

    const statusResulted = status === InterventionBookingStatus.RESULTED;
    const statusVerified = status === InterventionBookingStatus.VERIFIED;

    const statusResultedOrVerified = statusResulted || statusVerified;

    const isPcrTest = testIntervention === InterventionExternalCode.PCR_SALIVA_TEST
        || testIntervention === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST;
    const showCt = testResult === 'POSITIVE' && isPcrTest;

    const isRapidTest = testIntervention === InterventionExternalCode.RAPID_SALIVA_TEST;

    const disableVerifyTestResult = this.state.testResultInProgress
        || interventionBooking.status === InterventionBookingStatus.ON_HOLD;

    return <StyledForm onSubmit={handleSubmit}>
      <FormContainer>

        <div className="patient-data">
          {
            interventionBooking.patientId
                ? <div>
                  {`${interventionBooking.patientFirstName} ${interventionBooking.patientLastName}, ${isoToDisplayDate(interventionBooking.patientBirthDate)}`}
                </div>
                : <div>
                  {`${interventionBooking.poolCompanyDescription}`}
                </div>
          }
          <div className="barcode">
            {interventionBooking.testKitCode ? interventionBooking.testKitCode : ""}
          </div>
        </div>

        {
            !this.state.testResultSaveWarning && !this.state.testResultSaveError &&
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={6} className="radio-group" verticalAlign="middle">
                  <div className="field-label">
                    {getMessage("patientCentral.overview.tableHeader.testResult")}:
                  </div>

                  <Field
                      name="testResult"
                      component={RadioGroup}
                      radioDefinitions={testResults}
                      fluid
                      validate={required}
                      radiowidth="auto"
                      disabled={statusResultedOrVerified}
                  />
                </Grid.Column>

                {
                    showCt &&
                    <Grid.Column width={3} verticalAlign="middle">
                      <div className="field-label">
                        {getMessage("patientCentral.testResult.ct")}:
                      </div>

                      <Field
                          fluid
                          name="ct"
                          component={Input}
                          placeholder="##.##"
                          style={{width: "4rem"}}
                          validate={ctValidator}
                          parse={this.parseCtInput}
                          disabled={statusResultedOrVerified}
                      />
                      {
                          ctError &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.ctError`)}
                          </div>
                      }
                    </Grid.Column>
                }
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <div className="field-label">
                    <span className="inline-span">{getMessage("patientCentral.testResult.sampleDate")}:</span>
                    {
                        !statusResultedOrVerified &&
                        <span className="inline-span">
                      <CheckboxSc
                          checked={this.state.modifySampleDateTime}
                          label={getMessage("patientCentral.testResult.modifySampleDate")}
                          onChange={() => {
                            this.setState({modifySampleDateTime: !this.state.modifySampleDateTime})
                          }}
                      />
                    </span>
                    }
                  </div>

                  <div className="multi-field-row">
                    <div className="date-input">
                      <Field
                          name="testRegistrationDate"
                          component={DatePicker}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>

                    <div className="time-input">
                      <Field
                          name="testRegistrationTime"
                          size="mini"
                          component={TimeInput}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16} className="radio-group">
                  <div className="field-label">
                    <span className="inline-span">{getMessage("testSampleMethod")}:</span>
                  </div>
                  <Field
                      name="testSampleMethod"
                      component={RadioGroup}
                      radioDefinitions={this.state.sampleMethods}
                      fluid
                      radiowidth="auto"
                      disabled={statusResultedOrVerified}
                  />
                </Grid.Column>
              </Grid.Row>

              {
                  isRapidTest &&
                  <Grid.Row>
                    <Grid.Column width={6}>
                      <div className="field-label">
                        <span className="inline-span">{getMessage("patientCentral.checkIn.rapidTestCode")}:</span>
                      </div>
                      <Field
                          name="rapidTestCode"
                          component={Select}
                          options={this.state.rapidTestCodeOptions}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
              }

              {
                  isPcrTest &&
                  <>
                    <Grid.Row>
                      <Grid.Column width={6}>
                        <div className="field-label">
                          <span className="inline-span">{getMessage("patientCentral.testResult.laboratory")}:</span>
                        </div>
                        <Field
                            name="laboratoryId"
                            component={Select}
                            options={this.state.labOptions}
                            fluid
                            disabled={statusResultedOrVerified}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={6}>
                        <div className="field-label">
                          <span className="inline-span">{getMessage("patientCentral.testResult.analyzer")}:</span>
                        </div>
                        <Field
                            name="analyzerId"
                            component={Select}
                            options={this.state.analyzerOptions}
                            fluid
                            disabled={statusResultedOrVerified}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={6}>
                        <div className="field-label">
                          <span className="inline-span">{getMessage("patientCentral.testResult.remarks")}:</span>
                        </div>
                        <Field
                            name="remarks"
                            component={TextArea}
                            rows={2}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </>
              }
            </PraxFormGrid>
        }

        {
            this.state.testResultSaveWarning &&
            <div className="test-result-warning">
              {getMessage(this.state.testResultSaveWarning)}
            </div>
        }

        {
            this.state.errorMessage &&
            <StyledErrorMessage>{this.state.errorMessage}</StyledErrorMessage>
        }

        <FormButtonsRow>
          {
              (statusResulted && !statusVerified) && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    form.change("status", InterventionBookingStatus.AWAITING_COMPLETION);
                  }
                  }
                  disabled={this.state.testResultInProgress}
              >
                {getMessage("patientCentral.overview.changeTestResult.button")}
              </Button>
          }
          {
              statusResultedOrVerified && !interventionBooking.testResultVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    this.verifyTestKitResult(interventionBooking, values, undefined);
                  }}
                  disabled={disableVerifyTestResult}
              >
                {getMessage("patientCentral.overview.verifyTestResult.button")}
              </Button>
          }
          {
              !statusResultedOrVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!testResult || this.state.testResultInProgress}
                  onClick={() => {
                    if (!testResult) {
                      return;
                    }
                    this.setTestKitResult(interventionBooking, values, testResult);
                  }}
              >
                {getMessage("button.save")}
              </Button>
          }
          {
              statusVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!testResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!testResult) {
                      return;
                    }
                    this.setControlledBy();
                  }}
              >
                {getMessage("button.confirm")}
              </Button>
          }
          {
              statusVerified && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!testResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!testResult) {
                      return;
                    }
                    this.setResultRejected();
                  }}
              >
                {getMessage("button.reject")}
              </Button>
          }
          <Button compact type="button"
                  disabled={submitting}
                  onClick={() => {

                    const {testResultSaveWarning} = this.state;

                    if (testResultSaveWarning) {
                      this.closeAndUpdate();
                    } else {
                      this.props.onClose();
                    }
                  }}
          >
            {getMessage("action.back")}
          </Button>
        </FormButtonsRow>

        <br/>
        {
          previousTestResults && previousTestResults.length > 0
              ? <div className="previous-test-results">
                <CheckboxSc
                    checked={includingRevoked}
                    label={getMessage("patientRecord.testResult.includingRevoked")}
                    toggle={true}
                    onChange={() => this.handleCheckBoxChange()}
                />
                <Table compact="very">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage("patientRecord.testResult.externalCode")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("patientRecord.testResult.testKitCode")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("patientRecord.testResult.testingDate")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("patientRecord.testResult.testResult")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("patientRecord.testResult.resultDate")}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {
                      previousTestResults.map(previousTestResults => {
                        const isRevoked = previousTestResults.resultStatus === TestResultStatus.REVOKED
                        return (
                            <StyledTableRow
                                key={previousTestResults.id}
                                className={isRevoked ? "revoked" : ""}>
                              <Table.Cell>
                                {getMessage(`enum.InterventionExternalCode.${previousTestResults.externalCode}`)}
                                <Icon className={"resultStatusIcon"}
                                      color={isRevoked ? "grey" : "blue"}
                                      title={isRevoked ? "revoked" : "valid"}
                                      name="certificate"/>
                              </Table.Cell>
                              <Table.Cell>{previousTestResults.testKitCode}</Table.Cell>
                              <Table.Cell>{moment(previousTestResults.testingDate).format("DD.MM.YYYY HH:mm")}</Table.Cell>
                              <Table.Cell>{getMessage(`enum.testResult.${previousTestResults.testResult}`)}</Table.Cell>
                              <Table.Cell>{moment(previousTestResults.resultDate).format("DD.MM.YYYY HH:mm")}</Table.Cell>
                            </StyledTableRow>
                        )
                      })
                    }
                  </Table.Body>
                </Table>
              </div>
              : <div className="previous-test-results">
                <div className="no-results-message">{getMessage('patientCentral.testResult.noResults.message')}</div>
              </div>
        }
      </FormContainer>
    </StyledForm>;
  }

}

export default withMessages(withAuthContext(withPatientCentralRefDataContext(SetTestResultModal)));
